'use client';

import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
	useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import api, { getAccessToken } from '@/utils/api';

export type Notification = {
	_id: string;
	type: string;
	title: string;
	body?: string;
	link?: string;
	read: boolean;
	createdAt: string;
};

type NotificationContextValue = {
	notifications: Notification[];
	unreadCount: number;
	loading: boolean;
	markAsRead: (id: string) => Promise<void>;
	markAllRead: () => Promise<void>;
	open: boolean;
	setOpen: (v: boolean) => void;
};

const NotificationContext = createContext<
	NotificationContextValue | undefined
>(undefined);

const MAX_LIST = 100;

export function NotificationProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const { user } = useAuth();
	const [notifications, setNotifications] = useState<
		Notification[]
	>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const socketRef = useRef<WebSocket | null>(null);

	useEffect(() => {
		if (!user) {
			setNotifications([]);
			setUnreadCount(0);
			setLoading(false);
			return;
		}

		let disposed = false;
		let retry = 0;
		let retryTimer: ReturnType<typeof setTimeout> | null =
			null;
		let pollTimer: ReturnType<typeof setInterval> | null =
			null;

		const fetchNotifications = async () => {
			try {
				const resp = await api.get('/notifications', {
					params: { limit: MAX_LIST },
				});
				if (disposed) return;
				setNotifications(
					resp.data?.notifications ?? resp.data ?? [],
				);
				setUnreadCount(
					resp.data?.unreadCount ??
						(resp.data?.notifications ?? []).filter(
							(n: Notification) => !n.read,
						).length,
				);
			} catch {
				// Ignore transient fetch errors; retry via poll/WS
			} finally {
				if (!disposed) setLoading(false);
			}
		};

		const connect = () => {
			const wsUrl = process.env.NEXT_PUBLIC_WS_URL;
			const token = getAccessToken();
			if (!wsUrl || !token) {
				pollTimer = setInterval(
					fetchNotifications,
					60000,
				);
				fetchNotifications();
				return;
			}

			const proto = wsUrl.startsWith('https') ? 'wss' : 'ws';
			const url = `${wsUrl.replace(/^https?/, proto)}/notifications`;
			const socket = new WebSocket(url);
			socketRef.current = socket;

			socket.onopen = () => {
				retry = 0;
				fetchNotifications();
				socket.send(
					JSON.stringify({ type: 'auth', token }),
				);
			};

			socket.onmessage = (ev) => {
				try {
					const msg = JSON.parse(ev.data);
					if (msg.type === 'notification' && msg.data) {
						const n: Notification = msg.data;
						setNotifications((prev) =>
							prev.some((p) => p._id === n._id)
								? prev
								: [n, ...prev].slice(0, MAX_LIST),
						);
						if (!n.read)
							setUnreadCount((c) => c + 1);
					} else if (
						msg.type === 'unread_count' &&
						typeof msg.count === 'number'
					) {
						setUnreadCount(msg.count);
					} else if (msg.type === 'sync') {
						fetchNotifications();
					}
				} catch {
					// Ignore malformed frames
				}
			};

			socket.onclose = () => {
				if (disposed) return;
				socketRef.current = null;
				retry = Math.min(retry + 1, 8);
				const delay = Math.min(
					1000 * Math.pow(2, retry),
					30000,
				);
				retryTimer = setTimeout(connect, delay);
			};

			socket.onerror = () => socket.close();
		};

		fetchNotifications();
		connect();

		const onFocus = () => {
			if (!socketRef.current) fetchNotifications();
		};
		window.addEventListener('focus', onFocus);

		return () => {
			disposed = true;
			if (socketRef.current) socketRef.current.close();
			if (retryTimer) clearTimeout(retryTimer);
			if (pollTimer) clearInterval(pollTimer);
			window.removeEventListener('focus', onFocus);
		};
	}, [user]);

	const markAsRead = useCallback(async (id: string) => {
		setNotifications((prev) =>
			prev.map((n) =>
				n._id === id ? { ...n, read: true } : n,
			),
		);
		setUnreadCount((c) => Math.max(0, c - 1));
		try {
			await api.patch(`/notifications/${id}/read`);
		} catch {
			// Ignore; state will reconcile on next sync
		}
	}, []);

	const markAllRead = useCallback(async () => {
		setNotifications((prev) =>
			prev.map((n) => ({ ...n, read: true })),
		);
		setUnreadCount(0);
		try {
			await api.patch('/notifications/read-all');
		} catch {
			// Ignore; state will reconcile on next sync
		}
	}, []);

	const value: NotificationContextValue = {
		notifications,
		unreadCount,
		loading,
		markAsRead,
		markAllRead,
		open,
		setOpen,
	};

	return (
		<NotificationContext.Provider value={value}>
			{children}
		</NotificationContext.Provider>
	);
}

export function useNotifications() {
	const ctx = useContext(NotificationContext);
	if (!ctx) {
		throw new Error(
			'useNotifications must be used inside NotificationProvider',
		);
	}
	return ctx;
}
